<template>
	<div class="modal">
		<div class="row h-2">
			<div class="col-md-12 end-sm end-md end-lg">
				<a
					v-if="flowHistory?.flows?.length"
					:href="`/flows/${flowHistory?.flows[0].flow.id}`"
					class="jmiBtn blue mr-4"
				>
					Open in Editor
				</a>
				<a @click="close()"><span class="fa-light fa-circle-xmark textXlg textGrey"></span></a>
			</div>
		</div>
		<div class="pointer-events-none flex flex-col gap-2">
			<div class="w-full">
				<p class="textGrey textLg textCenter">Flow Viewer {{ completed ? ' - Call Ended' : '' }}</p>
			</div>
			<div class="realtime-header">
				<p v-if="callData.leg" class="uppercase">
					<strong class="font-bold normal-case">Leg:</strong> {{ callData.leg }}
				</p>
				<p v-if="callData.callFrom"><strong class="font-bold">From:</strong> {{ callData.callFrom }}</p>
				<p v-if="callData.callTo"><strong class="font-bold">To:</strong> {{ callData.callTo }}</p>
				<p v-if="callData.duration">
					<strong class="font-bold">Duration:</strong>
					{{ callData.duration }}s
				</p>
				<p v-if="callData.tenantName"><strong class="font-bold">Tenant:</strong> {{ callData.tenantName }}</p>
				<p v-if="callData.startTime">
					<strong class="font-bold">Start Time:</strong>
					{{ moment(callData.startTime).format('DD/MM/YYYY HH:mm:ss') }}
				</p>
				<p v-if="callData.connectedTime">
					<strong class="font-bold">End Time:</strong>
					{{ moment(callData.connectedTime).format('DD/MM/YYYY HH:mm:ss') }}
				</p>
			</div>
		</div>
		<div class="flex flex-col items-center justify-center gap-4 pt-4">
			<div
				v-if="flowHistory?.flows?.length > 0"
				class="relative mx-auto flex w-full items-center justify-center overflow-x-auto rounded-lg bg-white p-4 dark:bg-offmain"
				:style="{ 'min-height': `${flowHistory.flows.length * 54 + 72}px` }"
			>
				<jsplumb-toolkit
					v-if="flowNodes.length"
					ref="toolkitComponent"
					:surface-id="surfaceId"
					:render-params="renderParams()"
					:view="viewParams()"
					:toolkit-params="toolkitParams()"
					class="min-h-[77vh]"
				>
				</jsplumb-toolkit>
				<jsplumb-miniview :surface-id="surfaceId"></jsplumb-miniview>
			</div>
			<div v-else class="flex w-full items-center justify-center bg-white py-4 dark:bg-offmain">
				<h2 class="text-xl font-semibold">{{ loading ? 'Loading...' : 'Sorry, this call is unavailable' }}</h2>
			</div>
		</div>
		<div
			class="fixed bottom-4 left-1/2 flex -translate-x-1/2 transform items-center gap-2 rounded-full bg-gray-600 px-8 py-2 text-center text-white shadow-lg transition-all duration-300 ease-in"
			:class="{ 'translate-y-[200%] opacity-0': !showCompleted, 'translate-y-0 opacity-100': showCompleted }"
			@click="showCompleted = false"
		>
			<p>Call has ended</p>
			<span class="fa-light fa-close cursor-pointer"></span>
		</div>
	</div>
</template>

<script>
import { defineComponent } from 'vue'
import moment from 'moment'
import { loadSurface } from '@jsplumbtoolkit/browser-ui-vue3'
import {
	isPort,
	ForceDirectedLayout,
	DotEndpoint,
	LassoPlugin,
	AnchorLocations,
	CONNECTOR_TYPE_ORTHOGONAL
} from '@jsplumbtoolkit/browser-ui'
import { useRouter } from 'vue-router'

import BaseNode from '../components/flow/BaseNode.vue'
import NoteNode from '../components/flow/NoteNode.vue'
import flowsApi from '../scripts/flows/flows.api'
import cdrsApi from '../scripts/calldetailrecords/calldetailrecords.api'
import { markRaw } from 'vue'

let toolkitComponent
let toolkit
let surface

export default defineComponent({
	props: ['callData', 'refreshInterval', 'realtime', 'readonly', 'completed', 'cdrMode'],

	setup() {
		const router = useRouter()
		return { router }
	},

	data() {
		return {
			moment,
			loading: true,
			interval: null,
			flowNodes: [],
			flowHistory: null,
			activeNodeId: null,
			showCompleted: false,
			surfaceId: Date.now().toString(),
			completedStyle: {
				outlineColor: '#cdf27e',
				outlineWidth: 3,
				stroke: '#cdf27e',
				strokeWidth: 3
			}
		}
	},
	watch: {
		completed() {
			if (this.completed) {
				this.showCompleted = true
				setTimeout(() => {
					this.showCompleted = false
				}, 5000)
			}
		}
	},
	async mounted() {
		this.flowNodes = await flowsApi.getAllNodes()
		this.flowHistory = this.callData.flowHistory

		document.addEventListener('keyup', this.onKeyUp)

		toolkitComponent = this.$refs.toolkitComponent

		loadSurface(this.surfaceId, async (s) => {
			surface = s
			toolkit = surface.toolkitInstance

			if (this.flowHistory) {
				const flowData = this.flowHistory.flows[0].flow.data
				toolkit.load({ data: flowData, type: 'json' })
			}
			setTimeout(() => {
				surface.setZoomRange([0.25, 2])
				this.refresh()
			}, 100)

			if (parseInt(this.refreshInterval)) {
				this.interval = setInterval(() => {
					this.refresh()
				}, parseInt(this.refreshInterval))
			}
		})
	},
	beforeUnmount() {
		document.removeEventListener('keyup', this.onKeyUp)
		clearInterval(this.interval)
	},

	methods: {
		async refresh(forceRenderOnly) {
			if (!this.callData) return

			if (!forceRenderOnly) {
				if (this.completed && !this.cdrMode) {
					clearInterval(this.interval)

					setTimeout(async () => {
						try {
							const response = await cdrsApi.getByTeleflowId(this.callData.teleflowId)
							if (response.data?.flow_history) this.flowHistory = response.data.flow_history
						} catch (error) {
							console.error(error)
						}
						this.refresh(true)
					}, 2000)

					return
				}

				this.flowHistory = this.callData.flowHistory
			}

			let completedNodes = this.flowHistory.flows[0].history.filter((event) => event.type === 'nodecompleted')

			completedNodes.forEach((node, i) => {
				const n = toolkit.getNode(node.node.id)

				if (n) {
					n.data.history = {
						status: 'completed',
						outcome: node.outcome
					}
				}

				if (n.getPortTargetEdges().length > 0) {
					toolkit.updateEdge(
						this.findPreviousEdge(n, toolkit.getNode(completedNodes[i - 1].node.id)),
						this.completedStyle
					)
				}
			})

			const lastCompletedNode = completedNodes.pop()

			const startedNodes = this.flowHistory.flows[0].history.filter((event) => event.type === 'nodestarted')

			const activeNode = startedNodes.pop()

			toolkit.updateEdge(
				this.findPreviousEdge(toolkit.getNode(activeNode.node.id), toolkit.getNode(lastCompletedNode.node.id)),
				this.completedStyle
			)

			let activeNodeChanged = false

			if (this.activeNodeId !== activeNode.node.id) {
				activeNodeChanged = true
				this.activeNodeId = activeNode.node.id
			}

			const activeToolkitNode = toolkit.getNode(this.activeNodeId)

			if (activeToolkitNode) {
				activeToolkitNode.data.history = {
					status: this.completed ? 'final' : 'active',
					nodeCompletedEvent: activeNodeChanged ? lastCompletedNode : null,
					outcome:
						this.completed && activeToolkitNode.id === lastCompletedNode.node.id
							? lastCompletedNode.outcome
							: null
				}
			}
		},
		findPreviousEdge(node, previousNode) {
			const edges = node.getPortTargetEdges()

			if (!edges?.length) return null
			if (edges.length === 1) return edges[0].id

			try {
				if (previousNode) {
					const previousNodePortIds = previousNode.ports
						.filter((p) => p?.id?.indexOf('out:') === 0)
						.flatMap((p) => p.edges.map((e) => e?.id))

					const previousEdge = edges.find((e) => previousNodePortIds.includes(e?.id))

					if (previousEdge) return previousEdge.id
				}
			} catch (error) {
				console.error(error)
			}

			return edges[0].id
		},
		close() {
			this.$emit('close')
		},
		onKeyUp(event) {
			if (event.key == 'Escape') this.$emit('close')
		},
		toolkitParams() {
			return {
				beforeConnect: (source, target) => {
					return isPort(source) && isPort(target) && source !== target
				}
			}
		},
		viewParams() {
			const nodeList = {
				...this.flowNodes.reduce((acc, node) => {
					acc[node.type] = {
						component: markRaw(BaseNode),
						inject: {
							spec: node,
							flowNodes: this.flowNodes,
							isActive: (nodeId) => nodeId === this.activeNodeId,
							readonly: this.readonly
						}
					}
					return acc
				}, {}),
				note: {
					component: NoteNode,
					inject: {
						spec: {},
						readonly: this.readonly
					}
				}
			}

			return {
				nodes: nodeList,
				ports: {
					output: {
						edgeType: 'output',
						maxConnections: 1,
						anchor: [AnchorLocations.Right]
					},
					input: {
						edgeType: 'input',
						maxConnections: -1,
						anchor: [AnchorLocations.Left]
					}
				},
				edges: {
					default: {
						detachable: false,
						connector: {
							type: CONNECTOR_TYPE_ORTHOGONAL,
							options: {
								cornerRadius: 18
							}
						},
						paintStyle: {
							stroke: '#b0b0b0',
							strokeWidth: 3
						}
					}
				}
			}
		},
		renderParams() {
			return {
				wheel: {
					reverse: true
				},
				dragOptions: {
					filter: ['jtk-delete-button', 'jtk-add-button', 'jtk-schema-add'].join(',')
				},
				plugins: [LassoPlugin.type],
				zoomToFit: true,
				layout: {
					type: ForceDirectedLayout.type,
					options: {
						padding: { x: 150, y: 150 }
					}
				},
				zoomRange: [0.75, 1],
				defaults: {
					endpoint: {
						type: DotEndpoint.type,
						options: {
							cssClass: '.jtk-schema-endpoint'
						}
					}
				},
				consumeRightClick: false
			}
		},
		openInEditor() {
			if (this.flowHistory?.flows?.length) {
				const flowId = this.flowHistory.flows[0].flow.id
				this.router.push(`/flows/${flowId}`)
			}
		}
	}
})
</script>

<style scoped>
::-webkit-scrollbar {
	width: 0px !important;
}

.active-node {
	border: 2px solid red;
}

.slide-enter-active,
.slide-leave-active {
	transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
	transform: translateY(100%);
}

.slide-enter-to,
.slide-leave-from {
	transform: translateY(0);
}

.realtime-header {
	@apply flex flex-wrap items-center justify-center gap-x-8 gap-y-4 rounded-lg bg-white p-4 text-black dark:bg-offmain dark:text-white;
}

.realtime-header p {
	@apply whitespace-nowrap rounded-sm bg-black/5 px-4 py-2 text-sm font-semibold dark:bg-white/5;
}
</style>
